import React, { useState, useRef } from "react";
import { Table, Button } from "react-bootstrap";
import Papa from "papaparse";
import { toast } from "react-toastify";
import { db } from "../../firebase";
import { mapCsvRowToMeasurement } from "../../utils/measurementImportMapper";
import { formatWeightDisplay } from "../../utils/helpers";
import { formatMeasurementFeetInches } from "../../utils/feetInches";
import styles from "./MeasurementImport.module.scss";

// Parse a feet+inches label like 6'7" or 6'7.25" into total inches
const parseFeetInchesLabelToInches = (label) => {
  if (!label) return null;
  let s = String(label).trim();
  s = s.replace(/[’‘]/g, "'").replace(/[”“]/g, '"');
  const match = s.match(/(\d+)\s*'\s*([\d.]+)/);
  if (!match) return null;
  const feet = parseInt(match[1], 10);
  const inches = parseFloat(match[2]);
  if (isNaN(feet) || isNaN(inches)) return null;
  return feet * 12 + inches;
};

// Get a JS Date from various Firestore/JS date shapes
const getCreatedAtDate = (m) => {
  const v = m && m.createdAt;
  if (!v) return null;
  try {
    if (typeof v.toDate === "function") return v.toDate();
    if (v instanceof Date) return v;
    if (typeof v === "number") return new Date(v);
    if (typeof v === "string") {
      const d = new Date(v);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  } catch {
    return null;
  }
};

export default function MeasurementImport() {
  const [parsedRows, setParsedRows] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [activeTab, setActiveTab] = useState("import"); // 'import' | 'manage'
  const [measurements, setMeasurements] = useState([]);
  const [loadingMeasurements, setLoadingMeasurements] = useState(false);
  const [standingReachSort, setStandingReachSort] = useState(null); // 'asc' | 'desc' | null
  const [deletingAll, setDeletingAll] = useState(false);
  const fileInputRef = useRef(null);

  const handleDeleteAllMeasurements = async () => {
    if (!window.confirm("Delete ALL measurements? This cannot be undone. The next import will start with a clean database.")) return;
    setDeletingAll(true);
    try {
      const snapshot = await db.collection("player-measurements").get();
      const batchSize = 500;
      for (let i = 0; i < snapshot.docs.length; i += batchSize) {
        const batch = db.batch();
        snapshot.docs.slice(i, i + batchSize).forEach((doc) => batch.delete(doc.ref));
        await batch.commit();
      }
      setMeasurements([]);
      toast.success("All measurements deleted. Database is clean for the next import.");
    } catch (err) {
      console.error("Error deleting all measurements:", err);
      toast.error("Error deleting all measurements");
    } finally {
      setDeletingAll(false);
    }
  };

  const loadMeasurements = async () => {
    setLoadingMeasurements(true);
    try {
      const snapshot = await db.collection("player-measurements").get();
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      // Sort newest first by createdAt (fallback to measurement date)
      data.sort((a, b) => {
        const daDate = getCreatedAtDate(a) || (a.date ? new Date(a.date) : null);
        const dbDate = getCreatedAtDate(b) || (b.date ? new Date(b.date) : null);
        const da = daDate ? daDate.getTime() : 0;
        const db = dbDate ? dbDate.getTime() : 0;
        return db - da;
      });
      setMeasurements(data);
    } catch (err) {
      console.error("Error loading measurements:", err);
      toast.error("Error loading measurements");
    } finally {
      setLoadingMeasurements(false);
    }
  };

  const handleDeleteMeasurement = async (m) => {
    if (!window.confirm("Are you sure you want to delete this measurement?")) return;
    try {
      await db.collection("player-measurements").doc(m.id).delete();
      toast.success("Measurement deleted");
      await loadMeasurements();
    } catch (err) {
      console.error("Error deleting measurement:", err);
      toast.error("Error deleting measurement");
    }
  };

  const handleExportCsv = () => {
    if (!measurements || measurements.length === 0) {
      toast.error("No measurements to export.");
      return;
    }
    try {
      const rows = measurements.map((m) => ({
        "Player ID": m.playerProfile || "",
        "Player Name": m.playerName || "",
        Age: m.age || "",
        Event: m.event || m.source || "",
        "Event Date": m.date || "",
        "Body Fat %": m.bodyFat || "",
        "Hand L": m.handL || "",
        "Hand W": m.handW || "",
        "Height w/o shoes": m.heightNone || "",
        "Height w/ shoes": m.heightShoes || "",
        "w/ or w/o shoes not specified*": m.heightNotSpecified || "",
        "Lane Agility": m.laneAgility || m.handLA || "",
        Shuttle: m.shuttle || m.shuffle || "",
        Sprint: m.sprint || "",
        "Standing Reach": m.standingReach || "",
        "Vert Max": m.vertMax || "",
        "Vert No Step": m.vertNoStep || "",
        Weight: m.weight || "",
        Wingspan: m.wingspan || "",
        "Raw data": m.rawContent || m.raw || "",
      }));
      const csv = Papa.unparse(rows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `measurement-export-${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting CSV:", err);
      toast.error("Error exporting CSV");
    }
  };

  const handleFile = (file) => {
    if (!file || !file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please select a CSV file.");
      return;
    }
    setParseError(null);
    setImportResult(null);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors && results.errors.length > 0) {
          setParseError(results.errors[0].message || "CSV parse error");
          setParsedRows(null);
          return;
        }
        const rows = results.data || [];
        if (rows.length === 0) {
          setParseError("No rows found in CSV.");
          setParsedRows(null);
          return;
        }
        setParsedRows(rows);
        setParseError(null);
        toast.success(`Loaded ${rows.length} row(s). Review and click Import.`);
      },
      error: (err) => {
        setParseError(err.message || "Failed to parse CSV");
        setParsedRows(null);
      },
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileInputChange = (e) => {
    const file = e.target?.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  };

  const handleImport = async () => {
    if (!parsedRows || parsedRows.length === 0) {
      toast.error("No data to import. Drop a CSV first.");
      return;
    }
    setImporting(true);
    setImportResult(null);
    let success = 0;
    let failed = 0;
    const errors = [];
    for (let i = 0; i < parsedRows.length; i++) {
      const row = parsedRows[i];
      const payload = mapCsvRowToMeasurement(row);
      if (!payload.playerProfile) {
        failed++;
        errors.push(`Row ${i + 2}: missing Player ID`);
        continue;
      }
      try {
        await db.collection("player-measurements").add({
          ...payload,
          createdAt: new Date(),
        });
        success++;
      } catch (err) {
        failed++;
        errors.push(`Row ${i + 2}: ${err.message || "Write failed"}`);
      }
    }
    setImporting(false);
    setImportResult({ success, failed, errors: errors.slice(0, 10) });
    if (success > 0) toast.success(`Imported ${success} measurement(s).`);
    if (failed > 0) toast.error(`${failed} row(s) failed. Check summary below.`);
  };

  const mappedPreview = parsedRows
    ? parsedRows.slice(0, 20).map((row, idx) => mapCsvRowToMeasurement(row))
    : [];

  return (
    <div className={styles.wrapper}>
      <h1 className={styles.title}>Measurement Import</h1>

      <div className={styles.tabBar}>
        <button
          type="button"
          className={`${styles.tabButton} ${activeTab === "import" ? styles.tabButtonActive : ""}`}
          onClick={() => setActiveTab("import")}
        >
          Import CSV
        </button>
        <button
          type="button"
          className={`${styles.tabButton} ${activeTab === "manage" ? styles.tabButtonActive : ""}`}
          onClick={() => {
            setActiveTab("manage");
            if (!measurements.length) {
              loadMeasurements();
            }
          }}
        >
          View / Manage Measurements
        </button>
        <Button
          variant="danger"
          size="sm"
          className={styles.deleteAllButton}
          onClick={handleDeleteAllMeasurements}
          disabled={deletingAll}
        >
          {deletingAll ? "Deleting…" : "Delete all measurements"}
        </Button>
      </div>

      {activeTab === "import" && (
        <>
          <p className={styles.subtitle}>
            Drop a CSV file with measurement columns (Player ID, Player Name, Age, Event, Event Date, Body Fat %, Hand L, Hand W, Height w/o shoes, Height w/ shoes, w/ or w/o shoes not specified*, Lane Agility, Shuttle, Sprint, Standing Reach, Vert Max, Vert No Step, Weight, Wingspan, Raw data). Raw data is stored for backend records only.
          </p>

          <div
            className={styles.dropZone}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className={styles.fileInput}
              onChange={handleFileInputChange}
            />
            <p>Drop a CSV file here or click to browse</p>
            <p className={styles.hint}>CSV should match the parser export columns (Player ID, Event, Event Date, heights, w/ or w/o shoes not specified*, weight, etc.).</p>
          </div>

          {parseError && <p className={styles.error}>{parseError}</p>}

          {parsedRows && parsedRows.length > 0 && (
            <div className={styles.previewSection}>
              <p className={styles.rowCount}>
                {parsedRows.length} row(s) loaded. Showing first 20 below.
              </p>
              <div className={styles.previewTableWrap}>
                <Table bordered hover size="sm" className={styles.previewTable}>
                  <thead>
                    <tr>
                      <th>Player ID</th>
                      <th>Player Name</th>
                      <th>Event</th>
                      <th>Event Date</th>
                      <th>Age</th>
                      <th>Height w/o</th>
                      <th>Height w/</th>
                      <th>w/ or w/o not spec.*</th>
                      <th>Weight</th>
                      <th>Wingspan</th>
                      <th>Standing Reach</th>
                      <th>Hand L</th>
                      <th>Hand W</th>
                      <th>Raw (truncated)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappedPreview.map((row, idx) => {
                      const hShoesIn = parseFeetInchesLabelToInches(row.heightShoes);
                      const hNoneIn = parseFeetInchesLabelToInches(row.heightNone);
                      const invalidHeight =
                        hShoesIn != null &&
                        hNoneIn != null &&
                        hShoesIn < hNoneIn;

                      return (
                        <tr key={idx} className={invalidHeight ? styles.invalidRow : ""}>
                          <td>{row.playerProfile || "—"}</td>
                          <td>{row.playerName || "—"}</td>
                          <td>{(row.event || "").slice(0, 30)}{(row.event || "").length > 30 ? "…" : ""}</td>
                          <td>{row.date || "—"}</td>
                          <td>{row.age || "—"}</td>
                          <td>{formatMeasurementFeetInches(row.heightNone, "—")}</td>
                          <td>{formatMeasurementFeetInches(row.heightShoes, "—")}</td>
                          <td>{formatMeasurementFeetInches(row.heightNotSpecified, "—")}</td>
                          <td>{formatWeightDisplay(row.weight, "—")}</td>
                          <td>{formatMeasurementFeetInches(row.wingspan, "—")}</td>
                          <td>{formatMeasurementFeetInches(row.standingReach, "—")}</td>
                          <td>{row.handL || "—"}</td>
                          <td>{row.handW || "—"}</td>
                          <td className={styles.rawCell} title={row.rawContent}>
                            {(row.rawContent || "").slice(0, 60)}
                            {(row.rawContent || "").length > 60 ? "…" : ""}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              <div className={styles.actions}>
                <Button
                  variant="primary"
                  onClick={handleImport}
                  disabled={importing}
                >
                  {importing ? "Importing…" : `Import ${parsedRows.length} row(s)`}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === "import" && importResult && (
        <div className={styles.previewSection}>
          {importResult.success > 0 && (
            <p className={styles.success}>Imported {importResult.success} measurement(s).</p>
          )}
          {importResult.failed > 0 && (
            <>
              <p className={styles.error}>{importResult.failed} row(s) failed.</p>
              {importResult.errors?.length > 0 && (
                <ul className={styles.error}>
                  {importResult.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                  {importResult.errors.length < importResult.failed && (
                    <li>…and {importResult.failed - importResult.errors.length} more</li>
                  )}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === "manage" && (
        <div className={styles.previewSection}>
          <div className={styles.manageHeader}>
            <p className={styles.rowCount}>
              {loadingMeasurements
                ? "Loading measurements…"
                : `${measurements.length} measurement(s) found`}
            </p>
            <div className={styles.manageControls}>
              <Button
                variant="secondary"
                size="sm"
                onClick={loadMeasurements}
                disabled={loadingMeasurements}
                style={{ marginRight: "0.5rem" }}
              >
                {loadingMeasurements ? "Refreshing…" : "Refresh"}
              </Button>
              <Button
                variant="dark"
                size="sm"
                onClick={handleExportCsv}
                disabled={loadingMeasurements || !measurements.length}
                style={{ marginLeft: "0.5rem" }}
              >
                Export CSV
              </Button>
            </div>
          </div>
          <div className={styles.previewTableWrap}>
            <Table bordered hover size="sm" className={styles.previewTable}>
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Event Date</th>
                  <th>Event</th>
                  <th>Height w/o</th>
                  <th>Height w/</th>
                  <th>Weight</th>
                  <th>Wingspan</th>
                  <th>
                    <button
                      type="button"
                      className={styles.sortButton}
                      onClick={() =>
                        setStandingReachSort((prev) =>
                          prev === "asc" ? "desc" : prev === "desc" ? null : "asc"
                        )
                      }
                    >
                      Standing Reach
                      {standingReachSort === "asc"
                        ? " ↑"
                        : standingReachSort === "desc"
                        ? " ↓"
                        : ""}
                    </button>
                  </th>
                  <th>Vert Max</th>
                  <th>Vert No Step</th>
                  <th>Added</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  let rows = [...measurements];
                  if (standingReachSort) {
                    rows.sort((a, b) => {
                      const aIn = parseFeetInchesLabelToInches(a.standingReach);
                      const bIn = parseFeetInchesLabelToInches(b.standingReach);
                      const hasA = aIn != null;
                      const hasB = bIn != null;
                      if (!hasA && !hasB) return 0;
                      if (!hasA) return 1;
                      if (!hasB) return -1;
                      return standingReachSort === "asc" ? aIn - bIn : bIn - aIn;
                    });
                  }
                  return rows.map((m) => {
                  const createdAtDate = getCreatedAtDate(m);
                  const createdAtLabel = createdAtDate
                    ? createdAtDate.toLocaleString()
                    : "—";

                  return (
                    <tr key={m.id}>
                      <td>{m.playerName || "—"}</td>
                      <td>{m.date || "—"}</td>
                      <td>{m.event || m.source || "—"}</td>
                      <td>{formatMeasurementFeetInches(m.heightNone, "—")}</td>
                      <td>{formatMeasurementFeetInches(m.heightShoes, "—")}</td>
                      <td>{formatWeightDisplay(m.weight, "—")}</td>
                      <td>{formatMeasurementFeetInches(m.wingspan, "—")}</td>
                      <td>{formatMeasurementFeetInches(m.standingReach, "—")}</td>
                      <td>{m.vertMax || "—"}</td>
                      <td>{m.vertNoStep || "—"}</td>
                      <td>{createdAtLabel}</td>
                      <td>
                        <div className={styles.manageActions}>
                          {m.playerProfile && (
                            <Button
                              variant="outline-dark"
                              size="sm"
                              onClick={() =>
                                window.open(`/player-profile/detail/${m.playerProfile}`, "_blank")
                              }
                            >
                              Open Profile
                            </Button>
                          )}
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteMeasurement(m)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                  });
                })()}
                {measurements.length === 0 && !loadingMeasurements && (
                  <tr>
                    <td colSpan="11" style={{ textAlign: "center" }}>
                      No measurements found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
